import React from 'react';
import {
  emptyPerson,
  emptyManager,
  getDialCodeForBridgeCountry,
} from '../../utils/bridgeStaff';
import PersonFields from './PersonFields';

function PersonRowList({
  title,
  list,
  max,
  onChange,
  bridgeCountry,
  companyCountry,
  showCountryCode = true,
  lockFirst = false,
  allowRemoveFirst = false,
  emptyRowDial,
}) {
  const defaultDial = emptyRowDial || getDialCodeForBridgeCountry(bridgeCountry);
  const update = (idx, person) => {
    if (lockFirst && idx === 0) return;
    const next = [...list];
    next[idx] = person;
    onChange(next);
  };

  const add = () => {
    if (list.length >= max) return;
    onChange([...list, emptyPerson(defaultDial)]);
  };

  const remove = (idx) => {
    if (idx === 0 && !allowRemoveFirst) return;
    onChange(list.filter((_, i) => i !== idx));
  };

  return (
    <div className="staff-section">
      <h3 className="staff-section-title">{title}</h3>
      {list.map((person, idx) => (
        <div key={idx} className="staff-row">
          <div className={`staff-fields ${showCountryCode ? 'with-code' : ''}`}>
            <PersonFields
              person={person}
              onChange={(p) => update(idx, p)}
              bridgeCountry={bridgeCountry}
              companyCountry={companyCountry}
              showCountryCode={showCountryCode}
              readOnly={lockFirst && idx === 0}
            />
          </div>
          <div className="staff-row-actions">
            {list.length < max && (
              <button type="button" className="btn-staff btn-staff-add" onClick={add} title="Add row">
                +
              </button>
            )}
            {(idx > 0 || allowRemoveFirst) && (
              <button
                type="button"
                className="btn-staff btn-staff-remove"
                onClick={() => remove(idx)}
                title="Remove row"
              >
                −
              </button>
            )}
          </div>
        </div>
      ))}
      {list.length === 0 && allowRemoveFirst && (
        <div className="staff-row">
          <div className="staff-row-actions">
            <button type="button" className="btn-staff btn-staff-add" onClick={add} title="Add row">
              +
            </button>
          </div>
        </div>
      )}
      <p className="staff-hint">Max {max}</p>
    </div>
  );
}

function ManagerBlock({
  manager,
  index,
  onChange,
  onRemoveManager,
  canRemoveManager,
  bridgeCountry,
  companyCountry,
  emptyRowDial,
}) {
  const defaultDial = emptyRowDial || getDialCodeForBridgeCountry(bridgeCountry);
  const updateManager = (m) => onChange(m);

  const updateEngineer = (engIdx, eng) => {
    const engineers = [...(manager.engineers || [])];
    engineers[engIdx] = eng;
    onChange({ ...manager, engineers });
  };

  const addEngineer = () => {
    const engineers = [...(manager.engineers || [])];
    if (engineers.length >= 3) return;
    engineers.push(emptyPerson(defaultDial));
    onChange({ ...manager, engineers });
  };

  const removeEngineer = (engIdx) => {
    onChange({
      ...manager,
      engineers: (manager.engineers || []).filter((_, i) => i !== engIdx),
    });
  };

  return (
    <div className="manager-block">
      <div className="manager-block-header">
        <span>Bridge Manager {index + 1}</span>
        {canRemoveManager && (
          <button
            type="button"
            className="btn btn-gray btn-sm"
            onClick={onRemoveManager}
          >
            Remove Manager
          </button>
        )}
      </div>
      <div className="staff-row">
        <div className="staff-fields with-code">
          <PersonFields
            person={manager}
            onChange={updateManager}
            bridgeCountry={bridgeCountry}
            companyCountry={companyCountry}
            showCountryCode
          />
        </div>
        <div className="staff-row-actions" aria-hidden="true" />
      </div>

      <div className="engineers-block">
        <div className="engineers-header">
          <span>Bridge Engineers (optional)</span>
          {(manager.engineers || []).length < 3 && (
            <button type="button" className="btn btn-blue btn-sm" onClick={addEngineer}>
              + Add Engineer
            </button>
          )}
        </div>
        {(manager.engineers || []).map((eng, engIdx) => (
          <div key={engIdx} className="staff-row engineer-row">
            <div className="staff-fields with-code">
              <PersonFields
                person={eng}
                onChange={(p) => updateEngineer(engIdx, p)}
                bridgeCountry={bridgeCountry}
                companyCountry={companyCountry}
                showCountryCode
              />
            </div>
            <div className="staff-row-actions">
              <button
                type="button"
                className="btn-staff btn-staff-remove"
                onClick={() => removeEngineer(engIdx)}
                title="Remove engineer"
              >
                −
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ManagerList({ managers, onChange, bridgeCountry, companyCountry, emptyRowDial }) {
  const max = 6;
  const defaultDial = emptyRowDial || getDialCodeForBridgeCountry(bridgeCountry);

  const updateManager = (idx, m) => {
    const next = [...managers];
    next[idx] = m;
    onChange(next);
  };

  const addManager = () => {
    if (managers.length >= max) return;
    onChange([...managers, emptyManager(defaultDial)]);
  };

  const removeManager = (idx) => {
    onChange(managers.filter((_, i) => i !== idx));
  };

  return (
    <div className="staff-section">
      <h3 className="staff-section-title">Bridge Manager</h3>
      {managers.map((manager, idx) => (
        <div key={idx} className="manager-wrap">
          <ManagerBlock
            manager={manager}
            index={idx}
            onChange={(m) => updateManager(idx, m)}
            onRemoveManager={() => removeManager(idx)}
            canRemoveManager={idx > 0}
            bridgeCountry={bridgeCountry}
            companyCountry={companyCountry}
            emptyRowDial={emptyRowDial}
          />
          <div className="staff-row-actions manager-list-actions">
            {managers.length < max && (
              <button type="button" className="btn-staff btn-staff-add" onClick={addManager}>
                +
              </button>
            )}
            <button
              type="button"
              className="btn-staff btn-staff-remove"
              onClick={() => removeManager(idx)}
              title="Remove manager"
            >
              −
            </button>
          </div>
        </div>
      ))}
      {managers.length === 0 && (
        <div className="staff-row-actions manager-list-actions">
          <button type="button" className="btn-staff btn-staff-add" onClick={addManager} title="Add manager">
            +
          </button>
        </div>
      )}
      <p className="staff-hint">Max 6 managers</p>
    </div>
  );
}

export default function BridgeStaffForm({
  bridgeCountry,
  companyCountry,
  owners,
  admins,
  managers,
  onOwnersChange,
  onAdminsChange,
  onManagersChange,
  emptyRowDial,
}) {
  return (
    <div className="bridge-staff-form">
      <PersonRowList
        title="Bridge Admin"
        list={admins}
        max={3}
        bridgeCountry={bridgeCountry}
        companyCountry={companyCountry}
        onChange={onAdminsChange}
        showCountryCode
        lockFirst
        emptyRowDial={emptyRowDial}
      />
      <ManagerList
        managers={managers}
        bridgeCountry={bridgeCountry}
        companyCountry={companyCountry}
        onChange={onManagersChange}
        emptyRowDial={emptyRowDial}
      />
      <PersonRowList
        title="Bridge Owner"
        list={owners}
        max={3}
        bridgeCountry={bridgeCountry}
        companyCountry={companyCountry}
        onChange={onOwnersChange}
        showCountryCode
        allowRemoveFirst
        emptyRowDial={emptyRowDial}
      />
    </div>
  );
}
